import React, { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import BottomMenu from './common/components/BottomMenu';
import SocketController from './SocketController';
import InAppNotifications from './attention/InAppNotifications';
import { markAlertsRead } from './attention/unread';
import NavRail from './shell/NavRail';
import usePersistedState from './common/util/usePersistedState';
import { ZenyProvider } from './attention/ZenyProvider';
import AssistantWindow from './attention/AssistantWindow';
import { OperationsProvider } from './operations/OperationsProvider';
import PlatformPage from './operations/PlatformPage';
import TpmsShell from './tpms/TpmsShell';
import QuadrantLoader from './shell/QuadrantLoader';
import brand from './shell/brand';

const useStyles = makeStyles((muiTheme) => ({
  page: {
    flexGrow: 1,
    overflow: 'auto',
    minHeight: '100vh',
    transition: 'margin-left .18s ease',
    minWidth: 0,
    [muiTheme.breakpoints.down('md')]: { paddingBottom: muiTheme.dimensions.bottomBarHeight },
  },
  // The panel floats inside a gutter, so the space it occupies is its own width plus the gutter on
  // each side - the page clears all of it, not just the panel.
  pageWithRail: {
    marginLeft: muiTheme.dimensions.navRailWidth + muiTheme.dimensions.navRailInset * 2,
  },
  pageWithRailCollapsed: {
    marginLeft: muiTheme.dimensions.navRailWidthCollapsed + muiTheme.dimensions.navRailInset * 2,
  },
  menu: {
    zIndex: 1204,
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
  },
}));

const App = () => {
  const theme = useTheme();
  const account = useSelector((state) => state.session.user);
  const navigate = useNavigate();
  // Going to the exceptions page is the act of looking, so that is when the count of new ones goes
  // to zero. Every route into alerts - the bell, the phone menu, the alert itself - lands here.
  const openNotifications = () => { markAlertsRead(account); navigate('/exceptions'); };
  const classes = useStyles();

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const initialized = useSelector((state) => !!state.session.user);
  const user = useSelector((state) => state.session.user);
  const [railCollapsed, setRailCollapsed] = usePersistedState('navRailCollapsed', false);

  const railWidth = (() => {
    if (!desktop) return 0;
    const panel = railCollapsed
      ? theme.dimensions.navRailWidthCollapsed
      : theme.dimensions.navRailWidth;
    // Including the gutter on both sides, because this is what other fixed chrome insets past.
    return panel + theme.dimensions.navRailInset * 2;
  })();

  // Published as a CSS variable so fixed-position chrome elsewhere (the map
  // page's overlays, header and sidebar) can inset themselves past the rail
  // without each one importing and recomputing the same value.
  useEffect(() => {
    document.documentElement.style.setProperty('--nav-rail-width', `${railWidth}px`);
  }, [railWidth]);

  const pageClass = [
    classes.page,
    desktop ? (railCollapsed ? classes.pageWithRailCollapsed : classes.pageWithRail) : '',
  ].filter(Boolean).join(' ');

  if (initialized && user.role === 'PLATFORM_ADMINISTRATOR') return <PlatformPage />;
  // A tyre user gets the tyre application, not the operations shell with the map taken out. The
  // shell below loads the plant bootstrap and polls positions, both of which this role is refused,
  // so mounting it would give them a broken page and a stream of denied requests.
  if (initialized && user.role === 'TPMS_OPERATOR') return <TpmsShell />;

  return (
    <>
      <SocketController />
      {/* The only loading state this point of the application actually has is "is there a session
          yet". There are no named stages behind it, so it says one honest thing rather than
          inventing a sequence of them on a timer. */}
      {!initialized ? (<QuadrantLoader full message={`Loading ${brand.name}\u2026`} />) : (
        <OperationsProvider key={`${user.tenantId}:${user.id}`}>
          <ZenyProvider>
            {desktop && <NavRail onNotifications={openNotifications} collapsed={railCollapsed} onToggle={() => setRailCollapsed(!railCollapsed)} />}
            <div className={pageClass}>
              <Outlet />
            </div>
            {!desktop && (
            <div className={classes.menu}>
              <BottomMenu onNotifications={openNotifications} />
            </div>
            )}
            <InAppNotifications onReview={openNotifications} />
            {/* Asked from wherever somebody is, because a question about the plant is nearly always
                prompted by the screen they are already looking at. */}
            <AssistantWindow />
          </ZenyProvider>
        </OperationsProvider>
      )}
    </>
  );
};

export default App;
